'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, HTMLAttributes } from 'react';

import { cn } from '../../libs/utils';

const TabItemVariants = cva('flex w-full items-center justify-center', {
  variants: {
    variant: {
      segment: '',
      bar: ''
    },
    size: {
      xsmall: 'caption-medium-semibold',
      medium: 'body-medium-semibold'
    }
  },
  compoundVariants: [
    {
      size: 'medium',
      variant: 'segment',
      className: 'px-normal-medium py-tiny-large rounded-bzc-xsmall'
    },
    {
      size: 'xsmall',
      variant: 'segment',
      className: 'px-tiny-large rounded-bzc-2xsmall h-[22px]'
    },
    {
      size: 'xsmall',
      variant: 'bar',
      className: 'px-tiny-large'
    },
    {
      size: 'medium',
      variant: 'bar',
      className: 'px-tiny-large py-normal-small'
    }
  ],
  defaultVariants: {
    size: 'medium'
  }
});

export interface TabItemProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof TabItemVariants> {
  selected?: boolean;
  hiddenActiveBar?: boolean;
  label: string;
}

export const TabItem = forwardRef<HTMLDivElement, TabItemProps>(({ selected, size, variant, className, label, hiddenActiveBar = false, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        TabItemVariants({ size, variant }),
        {
          'elevation-small': selected && variant === 'segment',
          'hover:cursor-pointer': !selected
        },
        'relative',
        className
      )}
      {...props}
    >
      <p
        className={cn('select-none', {
          'text-neutral-foreground-main': selected,
          'text-neutral-foreground-lowest': !selected,
          'whitespace-nowrap': variant === 'bar'
        })}
      >
        {label}
      </p>
      {variant === 'bar' && selected && !hiddenActiveBar && <div className={'absolute bottom-0 h-[3px] w-full bg-neutral-foreground-main'} />}
    </div>
  );
});

TabItem.displayName = 'TabItem';
